import React, { useId } from 'react';
import whatsappIconSrc from '../assets/contact-whatsapp.png';
import phoneIconSrc from '../assets/contact-phone.png';
import telegramIconSrc from '../assets/contact-telegram.png';

const ICON_SIZE = 48;
const ICON_RADIUS = 12;

/** Shared square treatment so every brand icon (PNG or SVG) renders at the exact same box. */
function ContactIconImage({ src, size, className }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      width={size}
      height={size}
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: ICON_RADIUS,
        flexShrink: 0,
        display: 'block',
        objectFit: 'cover',
      }}
    />
  );
}

function WhatsAppBrandIcon({ size = ICON_SIZE, className = '' }) {
  return (
    <ContactIconImage src={whatsappIconSrc} size={size} className={className} />
  );
}

/** Logo ufficiale Telegram (PNG fornito), stessa cornice 48×48 degli altri brand. */
function TelegramBrandIcon({ size = ICON_SIZE, className = '' }) {
  return <ContactIconImage src={telegramIconSrc} size={size} className={className} />;
}

function InstagramBrandIcon({ size = ICON_SIZE }) {
  const gradientId = `ig-bg-${useId().replace(/:/g, '')}`;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FEDA75" />
          <stop offset="25%" stopColor="#FA7E1E" />
          <stop offset="50%" stopColor="#D62976" />
          <stop offset="75%" stopColor="#962FBF" />
          <stop offset="100%" stopColor="#4F5BD5" />
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx={ICON_RADIUS} fill={`url(#${gradientId})`} />
      <rect x="13" y="13" width="22" height="22" rx="6" fill="none" stroke="#fff" strokeWidth="2.2" />
      <circle cx="24" cy="24" r="5.2" fill="none" stroke="#fff" strokeWidth="2.2" />
      <circle cx="31.2" cy="16.8" r="1.6" fill="#fff" />
    </svg>
  );
}

/** Cornetta bianca su sfondo nero (PNG fornito), stessa cornice 48×48 degli altri brand. */
function PhoneBrandIcon({ size = ICON_SIZE, className = '' }) {
  return <ContactIconImage src={phoneIconSrc} size={size} className={className} />;
}

function EmailBrandIcon({ size = ICON_SIZE }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden style={{ flexShrink: 0 }}>
      <rect width="48" height="48" rx={ICON_RADIUS} fill="#EA4335" />
      <path fill="#FBBC04" d="M12 16h24v3.2L24 26 12 19.2V16z" />
      <path fill="#fff" d="M12 19.5 24 27l12-7.5V32H12V19.5z" />
      <path fill="#34A853" d="M36 16v16H12V32l12-7.5L36 32V16z" opacity="0.35" />
    </svg>
  );
}

function WebBrandIcon({ size = ICON_SIZE }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden style={{ flexShrink: 0 }}>
      <rect width="48" height="48" rx={ICON_RADIUS} fill="#4285F4" />
      <circle cx="24" cy="24" r="11" fill="none" stroke="#fff" strokeWidth="2" />
      <ellipse cx="24" cy="24" rx="5" ry="11" fill="none" stroke="#fff" strokeWidth="1.6" />
      <path
        d="M13 24h22M24 13c2.8 3.4 4.2 7.2 4.2 11s-1.4 7.6-4.2 11M24 13c-2.8 3.4-4.2 7.2-4.2 11s1.4 7.6 4.2 11"
        fill="none"
        stroke="#fff"
        strokeWidth="1.6"
      />
    </svg>
  );
}

const ContactBrandIcon = ({ kind, size = ICON_SIZE, className = '' }) => {
  const props = { size, className };
  switch (kind) {
    case 'phone':
      return <PhoneBrandIcon {...props} />;
    case 'email':
      return <EmailBrandIcon {...props} />;
    case 'whatsapp':
      return <WhatsAppBrandIcon {...props} />;
    case 'instagram':
      return <InstagramBrandIcon {...props} />;
    case 'telegram':
      return <TelegramBrandIcon {...props} />;
    case 'web':
    default:
      return <WebBrandIcon {...props} />;
  }
};

export default ContactBrandIcon;
